import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { SERVIZI, SETTORI } from "@/data/content";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/chi-siamo", label: "Chi Siamo" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-b border-foreground/10" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-5">
        <Logo />

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide underline-link ${isActive ? "text-primary" : "text-foreground/80 hover:text-foreground"}`
              }
            >
              {it.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-servizi-trigger" className="text-sm tracking-wide text-foreground/80 hover:text-foreground inline-flex items-center gap-1 outline-none">
              Servizi <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 rounded-none border-foreground/10">
              {SERVIZI.map((s) => (
                <DropdownMenuItem asChild key={s.slug} className="rounded-none py-3">
                  <Link to={`/servizi#${s.slug}`} data-testid={`nav-servizio-${s.slug}`}>
                    <span className="flex flex-col">
                      <span className="font-display text-base">{s.title}</span>
                      <span className="text-xs text-muted-foreground">{s.short}</span>
                    </span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-settori-trigger" className="text-sm tracking-wide text-foreground/80 hover:text-foreground inline-flex items-center gap-1 outline-none">
              Settori <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 rounded-none border-foreground/10 grid grid-cols-1">
              {SETTORI.map((s) => (
                <DropdownMenuItem asChild key={s.slug} className="rounded-none py-3">
                  <Link to={`/settori/${s.slug}`} data-testid={`nav-settore-${s.slug}`}>
                    <span className="flex flex-col">
                      <span className="font-display text-base">{s.title}</span>
                      <span className="text-xs text-muted-foreground line-clamp-1">{s.short}</span>
                    </span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/assistart" data-testid="nav-assistart" className="text-sm tracking-wide underline-link text-foreground/80 hover:text-foreground">
            Assistart
          </NavLink>
          <NavLink to="/contatti" data-testid="nav-contatti" className="text-sm tracking-wide underline-link text-foreground/80 hover:text-foreground">
            Contatti
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.email ? (
            <Link to="/area-riservata" data-testid="nav-dashboard-link" className="btn-primary">
              Area Riservata
            </Link>
          ) : (
            <Link to="/accedi" data-testid="nav-login-link" className="btn-primary">
              Area Riservata
            </Link>
          )}
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="lg:hidden p-2 -mr-2" aria-label="Apri menu">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:w-96 bg-background border-l border-foreground/10">
            <SheetHeader>
              <SheetTitle className="font-display text-2xl">Menu</SheetTitle>
            </SheetHeader>
            <div className="mt-10 flex flex-col gap-1">
              {navItems.map((it) => (
                <Link key={it.to} to={it.to} className="py-3 text-lg font-display border-b border-foreground/10" data-testid={`mobile-nav-${it.label.toLowerCase()}`}>
                  {it.label}
                </Link>
              ))}
              <Link to="/servizi" className="py-3 text-lg font-display border-b border-foreground/10" data-testid="mobile-nav-servizi">
                Servizi
              </Link>
              <Link to="/settori" className="py-3 text-lg font-display border-b border-foreground/10" data-testid="mobile-nav-settori">
                Settori
              </Link>
              <Link to="/assistart" className="py-3 text-lg font-display border-b border-foreground/10" data-testid="mobile-nav-assistart">
                Assistart
              </Link>
              <Link to="/contatti" className="py-3 text-lg font-display border-b border-foreground/10" data-testid="mobile-nav-contatti">
                Contatti
              </Link>
              <Link
                to={user && user.email ? "/area-riservata" : "/accedi"}
                data-testid="mobile-nav-login"
                className="btn-primary mt-8 w-full"
              >
                Area Riservata
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
